import {
  Box,
  FormControl,
  Grid2,
  MenuItem,
  Select,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import { ErrorMessage, useFormikContext } from "formik";
import { useEffect, useState } from "react";
import {
  getDropdownCity,
  getDropdownState,
  getDropdownZipCode,
  getStorageValue,
  setStorageValue
} from "../../utils/helper";

const PickupAddressForm = ({
  handleChange,
  values,
  touched,
  errors,
  setFieldTouched,
  setFieldValue,
}) => {
  const [cities, setCities] = useState([]);
  const [states, setStates] = useState([]);
  const [zipCodes, setZipCodes] = useState([]);
  const [stateSearchTerm, setStateSearchTerm] = useState("");
  const [citySearchTerm, setCitySearchTerm] = useState("");
  const [zipcodeSearchTerm, setZipcodeSearchTerm] = useState("");

  const { isSubmitting, isValidating } = useFormikContext();
  
    useEffect(() => {
      if (isSubmitting && !isValidating) {
        let keys = Object.keys(errors);
        if (keys.length > 0) {
          const selector = `[name=${keys[0]}]`;
          const errorElement = document.querySelector(selector);
          if (errorElement) {
            errorElement.focus();
          }
        }
      }
    }, [errors, isSubmitting, isValidating]);

  useEffect(() => {
    setdropdownState();
    if (values.pickupState && values.pickupCity && values.pickupZipcode) {
      setdropdownCity(values.pickupState);
      setdropdownZipcode(values.pickupState, values.pickupCity);
    }
    // eslint-disable-next-line
  }, [values.pickupState]);

  /**
   * fetches dropdowns values of state from api
   */
  const setdropdownState = async () => {
    const response = await getDropdownState();
    // setFieldTouched("pickupZipcode", false);
    setStates(response.data?.state);
  };

  /**
   * fetches dropdowns values of city from api
   * @param {string} state - chosen state
   */
  const setdropdownCity = async (state) => {
    const response = await getDropdownCity(state);
    // setFieldTouched("pickupZipcode", false);
    setCities(response.data?.city);
    if (response.data?.city.length === 1) {
      handleChange({
        target: { name: "pickupCity", value: response.data?.city[0] },
      });
      setdropdownZipcode(state, response.data?.city[0]);
    }
  };

  useEffect(() => {
    setStorageValue("fieldValues", JSON.stringify(values));
  }, [values])

  /**
   * fetches dropdowns values of zipCodes from api
   * @param {string} state - chosen state
   * @param {*} city - chosen city
   */
  const setdropdownZipcode = async (state, city) => {
    const result = await getDropdownZipCode(state, city);
    setFieldTouched("pickupZipcode", false);
    setZipCodes(result?.data?.zipCode || "");
    const updatedValues = JSON.parse(getStorageValue("fieldValues"));
    if (result?.data?.zipCode?.length === 1 && updatedValues?.pickupState) {
      handleChange({
        target: { name: "pickupZipcode", value: result?.data?.zipCode[0] || "" },
      });
    }
  };

  /**
   * handle state dropdown search
   * @param {*} event
   */
  const handleStateSearchChange = (event) => {
    setStateSearchTerm(
      event.target.value?.replace(/[^a-zA-Z ]+/gi, "")?.replace(/\s\s+/g, " ")?.trimStart()
    );
  };

  // Filtered options based on search term
  const filteredStates = states?.filter(
    (state) =>
      state.state.toLowerCase().startsWith(stateSearchTerm.toLowerCase()) ||
      state.stateCode.toLowerCase().startsWith(stateSearchTerm.toLowerCase())
  );

  /**
   * handle city dropdown search
   * @param {*} event
   */
  const handleCitySearchChange = (event) => {
    setFieldTouched("pickupZipcode", false);
    setCitySearchTerm(
      event.target.value?.replace(/[^a-zA-Z ]+/gi, "")?.replace(/\s\s+/g, " ")?.trimStart()
    );
  };

    /**
   * handle zipcode dropdown search
   * @param {*} event
   */
    const handleZipcodeSearchChange = (event) => {
      setZipcodeSearchTerm(event.target.value?.replace(/\s\s+/g, ' ')?.replace(/[^0-9]+/ig, ""));
    };

    // Filtered options based on search term
  const filteredZipcodes = zipCodes?.filter((zipValue) =>
    zipValue?.toLowerCase().startsWith(zipcodeSearchTerm?.toLowerCase())
  );

  // Filtered options based on search term
  const filteredCities = cities?.filter((city) =>
    city?.toLowerCase().startsWith(citySearchTerm?.toLowerCase())
  );

  const handleStateChange = (state) => {
    setFieldTouched("pickupState", false)
    handleChange({ target: { name: "pickupCity", value: "" } });
    handleChange({
      target: { name: "pickupZipcode", value: "" },
    });
    setdropdownCity(state);
  };

  const handleCityChange = async (state, city) => {
    if (!state || !city) return;
    setFieldTouched("pickupZipcode", false);

    handleChange({
      target: { name: "pickupZipcode", value: "" },
    });
    setdropdownZipcode(state, city);
  };

  const handlePickupInfo = (isChecked) => {
    const userProfile = JSON?.parse(getStorageValue('userInfo')) ;
    setFieldValue("pickupFullName", isChecked ? userProfile?.firstName + " " + userProfile?.lastName : "");
    setFieldValue("pickupAddress", isChecked ? userProfile?.address?.street : "");
    setFieldValue("pickupPhoneNumber", isChecked ? userProfile?.phoneNumber : "");
    setFieldValue("pickupCity", isChecked ? userProfile?.address?.city : "");
    setFieldValue("pickupState", isChecked ? userProfile?.address?.state : "");
    setFieldValue("pickupZipcode", isChecked ? userProfile?.address?.zipCode : "");
    setFieldValue("autofillPickupAddress", isChecked)

    setFieldTouched("pickupFullName", false);
    setFieldTouched("pickupAddress", false);
    setFieldTouched("pickupPhoneNumber", false);
    setFieldTouched("pickupCity", false);
    setFieldTouched("pickupState", false);
    setFieldTouched("pickupZipcode", false);
  }

  return (
    <>
      <div>
        <Typography sx={{ mt: 2, mb: 1 }} variant="h6">
          Pickup Address:
        </Typography>
        <FormControlLabel
          sx={{ paddingBottom: "10px", color: '#344054' }}
          label="Use my profile address"
          slotProps={{
            typography: {
              fontFamily: "poppins !important",
              fontSize: "15px",
              lineHeight: 1,
              fontWeight: 500
            }
          }}
          control={
            <Checkbox
              size="small"
              name="autofillPickupAddress"
              checked={values?.autofillPickupAddress}
              onChange={(e) => {
                handleChange({
                  target: {
                    name: "autofillPickupAddress",
                    value: e?.target?.checked
                  },
                })
                handlePickupInfo(e?.target?.checked);
              }
              }
            />
          }
        />
        <Grid2 container>
          <Grid2
            sx={{ minHeight: "80px" }}
            size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "14px",
                fontFamily: "poppins!important",
              }}
              className="form-label required"
            >
              Full name
            </Typography>
            <Grid2>
              <TextField
                sx={{ borderRadius: "8px" }}
                name="pickupFullName"
                fullWidth
                variant="outlined"
                size="small"
                type="text"
                placeholder="Enter Full Name"
                slotProps={{ htmlInput: { maxLength: 50 } }}
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "pickupFullName",
                      value: e?.target?.value
                        ?.replace(/[^a-zA-Z ]+/gi, "")
                        ?.replace(/\s\s+/g, " ")?.trimStart(),
                    },
                  })
                }
                value={values.pickupFullName}
                error={touched.pickupFullName && Boolean(errors.pickupFullName)}
              />
              <ErrorMessage
                name="pickupFullName"
                component="div"
                className="error-messages"
              />
            </Grid2>
          </Grid2>
          <Grid2
            sx={{ minHeight: "80px" }}
            size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "14px",
                fontFamily: "poppins!important",
              }}
              className="form-label required"
            >
              Phone number
            </Typography>
            <Grid2 size={{ xs: 12, sm: 12, md: 5, lg: 5 }}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  sx={{ borderRadius: "8px", color: "#667085" }}
                  variant="outlined"
                  size="small"
                  type="text"
                  value="+1"
                  className="country-code"
                />
                <TextField
                  name="pickupPhoneNumber"
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter Phone number"
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "pickupPhoneNumber",
                        value: e?.target?.value?.replace(/\D/g, "")
                      },
                    })
                  }
                  value={values.pickupPhoneNumber}
                  onInput={(e) => {
                    e.target.value = Math.max(0, parseInt(e.target.value))
                      .toString()
                      .slice(0, 10);
                  }}
                  error={
                    touched.pickupPhoneNumber &&
                    Boolean(errors.pickupPhoneNumber)
                  }
                />
              </Box>
              <ErrorMessage
                name="pickupPhoneNumber"
                component="div"
                className="error-messages"
              />
            </Grid2>
          </Grid2>
          <Grid2
            sx={{ minHeight: "80px" }}
            size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "14px",
                fontFamily: "poppins!important",
              }}
              className="form-label required"
            >
              Address
            </Typography>
            <Grid2>
              <TextField
                name="pickupAddress"
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Enter Address here"
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "pickupAddress",
                      value: e?.target?.value?.replace(/[^a-zA-Z0-9\s.,#\-/;'"_&]+/gi, "")?.replace(/\s\s+/g, " ")?.trimStart(),
                    },
                  })
                }
                value={values.pickupAddress}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                error={touched.pickupAddress && Boolean(errors.pickupAddress)}
              />
              <ErrorMessage
                name="pickupAddress"
                component="div"
                className="error-messages"
              />
            </Grid2>
          </Grid2>
          <Grid2
            sx={{ minHeight: "80px" }}
            container
            spacing={2}
            size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
          >
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
              <Typography
                className="form-label required"
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  fontFamily: "poppins!important",
                }}
              >
                State
              </Typography>
              <FormControl fullWidth size="small" variant="outlined">
                <Select
                  name="pickupState"
                  value={values?.pickupState || ""}
                  onChange={(event) => {
                    handleChange(event);
                    handleStateChange(
                      event?.target?.value
                        ?.replace(/[^a-zA-Z ]+/gi, "")
                        ?.replace(/\s\s+/g, " ")?.trimStart()
                    );
                  }}
                  displayEmpty
                  onOpen={() => {
                    setStateSearchTerm("")
                  }}
                  renderValue={(selected) =>
                    selected ? (
                      selected
                    ) : (
                      <span style={{ color: "#A6ADB7" }}>Select state</span>
                    )
                  }
                  error={touched.pickupState && Boolean(errors.pickupState)}
                  MenuProps={{autoFocus: false}}
                >
                  <MenuItem
                    className="dropdown-search"
                    disableRipple
                    sx={{ background: "transparent" }}
                    value={values.pickupState}
                  >
                    <TextField
                      placeholder="Search state"
                      size="small"
                      sx={{ 
                        outline: "none", 
                        border: "none",
                        "& fieldset": { border: 'none' },
                      }}
                      fullWidth
                      value={stateSearchTerm}
                      slotProps={{htmlInput: {maxLength: 30}}}
                      onChange={handleStateSearchChange}
                      autoFocus
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => {
                        if (event.key !== "Escape") {
                          event.stopPropagation();
                        }
                      }}
                    />
                  </MenuItem>
                  {filteredStates?.length > 0 ? (
                    filteredStates?.map((state) => (
                      <MenuItem key={state.state} value={state.state}>
                        {state.state}
                      </MenuItem>
                    ))
                  ) : filteredStates?.length === 0 && values?.pickupState ? (
                    <MenuItem value={values?.pickupState}>
                      No results found
                    </MenuItem>
                  ) : (
                    <MenuItem disabled>No results found</MenuItem>
                  )}
                </Select>
              </FormControl>
              <ErrorMessage
                name="pickupState"
                component="div"
                className="error-messages"
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
              <Typography
                className="form-label required"
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  fontFamily: "poppins!important",
                }}
              >
                City
              </Typography>
              <FormControl fullWidth size="small" variant="outlined">
                <Select
                  name="pickupCity"
                  autoComplete="off"
                  value={values?.pickupCity ?? ""}
                  onChange={(event) => {
                    handleChange(event);
                    handleCityChange(
                      values?.pickupState,
                      event.target?.value
                        ?.replace(/[^a-zA-Z ]+/gi, "")
                        ?.replace(/\s\s+/g, " ")?.trimStart()
                    );
                  }}
                  onOpen={() => {
                    setCitySearchTerm("");
                  }}
                  sx={{
                    "&.MuiOutlinedInput-root.Mui-disabled": {
                      "& fieldset": {
                        borderColor: touched.pickupCity && Boolean(errors.pickupCity) ? "#d32f2f" : "#d2d7df"
                      },
                    }
                  }}
                  disabled={!values?.pickupState}
                  displayEmpty
                  renderValue={(selected) =>
                    selected ? (
                      selected
                    ) : (
                      <span style={{ color: "#A6ADB7" }}>Select city</span>
                    )
                  }
                  error={touched.pickupCity && Boolean(errors.pickupCity)}
                  MenuProps={{autoFocus: false}}
                >
                  {values.pickupState && (
                    <MenuItem
                      className="dropdown-search"
                      disableRipple
                      sx={{ background: "transparent" }}
                      value={values.pickupCity}
                    >
                      <TextField
                        placeholder="Search City"
                        size="small"
                        sx={{ 
                          outline: "none", 
                          border: "none",
                          "& fieldset": { border: 'none' },
                        }}
                        fullWidth
                        value={citySearchTerm}
                        slotProps={{htmlInput: {maxLength: 30}}}
                        onChange={handleCitySearchChange}
                        autoFocus
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => {
                          if (event.key !== "Escape") {
                            event.stopPropagation();
                          }
                        }}
                      />
                    </MenuItem>
                  )}
                  {filteredCities?.length > 0 && values.pickupState ? (
                    filteredCities?.map((city) => (
                      <MenuItem key={city} value={city ?? ""}>
                        {city}
                      </MenuItem>
                    ))
                  ) : filteredCities?.length === 0 && values?.pickupCity ? (
                    <MenuItem value={values?.pickupCity}>
                      No results found
                    </MenuItem>
                  ) : (
                    <MenuItem disabled>No results found</MenuItem>
                  )}
                </Select>
              </FormControl>

              <ErrorMessage
                name="pickupCity"
                component="div"
                className="error-messages"
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  fontFamily: "poppins!important",
                }}
                className="form-label required"
              >
                Zipcode
              </Typography>
              <FormControl fullWidth size="small" variant="outlined">
                <Select
                  name="pickupZipcode"
                  autoComplete="off"
                  value={values?.pickupZipcode || ""}
                  onChange={(event) => {
                    handleChange(event);
                    setFieldTouched("pickupZipcode", false);
                  }}
                  displayEmpty
                  onOpen={() => {
                    setZipcodeSearchTerm("");
                  }}
                  sx={{
                    "&.MuiOutlinedInput-root.Mui-disabled": {
                      "& fieldset": {
                        borderColor: touched.pickupZipcode && Boolean(errors.pickupZipcode) ? "#d32f2f" : "#d2d7df"
                      },
                    }
                  }}
                  disabled={!values?.pickupState || !values?.pickupCity}
                  renderValue={(selected) =>
                    selected ? (
                      selected
                    ) : (
                      <span style={{ color: "#A6ADB7" }}>Select zipcode</span>
                    )
                  }
                  error={touched.pickupZipcode && Boolean(errors.pickupZipcode)}
                  MenuProps={{autoFocus: false}}
                >
                  {(
                    <MenuItem
                      className="dropdown-search"
                      disableRipple
                      sx={{ background: "transparent" }}
                      value={values.pickupZipcode}
                    >
                      <TextField
                        placeholder="Search Zipcode"
                        size="small"
                        sx={{ 
                          outline: "none", 
                          border: "none",
                          "& fieldset": { border: 'none' },
                        }}
                        fullWidth
                        value={zipcodeSearchTerm}
                        slotProps={{htmlInput: {maxLength: 30}}}
                        onChange={handleZipcodeSearchChange}
                        autoFocus
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => {
                          if (event.key !== "Escape") {
                            event.stopPropagation();
                          }
                        }} 
                      />
                    </MenuItem>
                  )}
                  {filteredZipcodes?.length > 0 ? (
                    filteredZipcodes?.map((zipCode) => (
                      <MenuItem key={zipCode} value={zipCode}>
                        {zipCode}
                      </MenuItem>
                    ))
                  ) : filteredZipcodes?.length === 0 && values?.pickupZipcode ? (
                    <MenuItem value={values?.pickupZipcode}>
                      No results found
                    </MenuItem>
                  ) : (
                    <MenuItem disabled>No results found</MenuItem>
                  )}
                </Select>
              </FormControl>

              <ErrorMessage
                name="pickupZipcode"
                component="div"
                className="error-messages"
              />
            </Grid2>
          </Grid2>
        </Grid2>
      </div>
    </>
  );
};

export default PickupAddressForm;
